/**
 * useOverlayBackClose - Make the browser / hardware back button close an overlay
 * instead of leaving the current route.
 *
 * When `open` flips true we push a sentinel history entry and register the
 * overlay on a module-level LIFO stack. A single shared `popstate` listener
 * dispatches the event to the topmost entry only so stacked overlays (e.g.
 * messages list + open thread) unwind one tap at a time instead of all
 * closing at once. When an overlay is dismissed by code (backdrop tap,
 * header "← Back", swipe, Escape, etc.) we roll the sentinel entry back off
 * so the forward/back stack stays aligned with what the user sees.
 */

import { useEffect, useRef } from 'react';

// Module-level state shared across every overlay on the page.
const stack = [];
let skipNext = 0;
let installed = false;

function installListener() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('popstate', () => {
    if (skipNext > 0) {
      // Programmatic close fired this popstate to undo its sentinel entry —
      // swallow it so a DIFFERENT overlay doesn't accidentally close.
      skipNext -= 1;
      return;
    }
    const top = stack[stack.length - 1];
    if (!top) return;
    top.closed = true;
    stack.pop();
    try {
      top.onClose?.();
    } catch {
      // never let a consumer error leave the stack in a bad state
    }
  });
}

function useOverlayBackClose(open, onClose) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === 'undefined' || !window.history?.pushState) return undefined;

    installListener();
    const entry = { closed: false, onClose: () => onCloseRef.current?.() };
    stack.push(entry);
    window.history.pushState({ overlayBack: true }, '');

    return () => {
      const idx = stack.indexOf(entry);
      if (idx !== -1) stack.splice(idx, 1);
      if (entry.closed) return;
      // Programmatic dismissal. Only pop the sentinel if we're still AT it —
      // if the overlay is closing because something inside it navigated
      // forward (e.g. a MobileDrawer link, a logout redirect), the browser
      // has already pushed past our sentinel and calling history.back() would
      // undo that navigation and drop the user on the previous page.
      if (window.history.state?.overlayBack === true) {
        skipNext += 1;
        window.history.back();
      }
    };
  }, [open]);
}

/** Test-only: reset module state between tests. */
export function __resetOverlayBackCloseForTests() {
  stack.length = 0;
  skipNext = 0;
}

export default useOverlayBackClose;
